import { InstallPrompt } from '@/components/pwa/install-prompt'
import { PWAStatus } from '@/components/pwa/pwa-status'
import { UpdateBanner } from '@/components/pwa/update-banner'
import { PWAContext, type PWAContextType } from '@/contexts/pwa.context'
import { useEffect, useRef, useState } from 'react'

interface PWAProviderProps {
  children: React.ReactNode
}

export function PWAProvider({ children }: PWAProviderProps) {
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [isInstalled, setIsInstalled] = useState(false)
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const [needRefresh, setNeedRefresh] = useState(false)
  const updateCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const updateServiceWorker = (reloadPage?: boolean) => {
    if (!('serviceWorker' in navigator)) return
    navigator.serviceWorker.getRegistration().then((registration) => {
      if (!registration?.waiting) return
      // Reload only once the new worker takes control, otherwise the reload is
      // served the old precached shell (same fix as use-app-update's refresh).
      if (reloadPage) {
        navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), {
          once: true,
        })
      }
      registration.waiting.postMessage({ type: 'SKIP_WAITING' })
    })
  }

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Check if app is installed
    if (window.matchMedia('(display-mode: standalone)').matches) setIsInstalled(true)

    // Check for iOS standalone mode
    if ('standalone' in window.navigator && window.navigator['standalone'] === true) setIsInstalled(true)

    // Register service worker and check for updates periodically so deployments
    // are detected without requiring a full page reload (e.g. after SPA navigation).
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/sw.js', { scope: '/' })
        .then((registration) => {
          console.log('SW registered: ', registration)
          // When periodic update() finds a new deployment, a waiting worker appears
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) setNeedRefresh(true)
              })
            }
          })
          // Check for new deployment every 60s so users see updates without hard refresh
          updateCheckIntervalRef.current = setInterval(() => {
            registration.update().catch(() => {})
          }, 60 * 1000)
        })
        .catch((registrationError) => {
          console.log('SW registration failed: ', registrationError)
        })
    }

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      if (updateCheckIntervalRef.current) clearInterval(updateCheckIntervalRef.current)
    }
  }, [])

  useEffect(() => {
    setUpdateAvailable(needRefresh)
  }, [needRefresh])

  const contextValue: PWAContextType = {
    isOnline,
    isInstalled,
    updateAvailable,
    updateServiceWorker: () => {
      updateServiceWorker(true)
      setNeedRefresh(false)
    },
  }

  return (
    <PWAContext.Provider value={contextValue}>
      {children}
      <UpdateBanner />
      <PWAStatus />
      <InstallPrompt />
    </PWAContext.Provider>
  )
}
